import { useSelf } from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import api from "@rentiohq/shared-frontend/dist/utils/api/api.utils";
import {
  getLocale,
  getLocalizedText,
} from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  TextStyle,
  Loading,
  ELoadingSize,
  Modal,
  Tooltip,
} from "@rentiohq/web-shared/dist/components";
import { toast } from "@rentiohq/web-shared/dist/managers/Toast";
import styled from "@rentiohq/web-theme";
import moment from "moment";
import "moment/locale/nl-be";
import "moment/locale/fr";
import "moment/locale/en-gb";
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
// @ts-ignore
import { Calendar, momentLocalizer } from "react-big-calendar";
// @ts-ignore
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import { RM_COLORS } from "../rentmatch.colors";
import { WarningTooltip } from "./WarningTooltip";

const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);

// ========== STYLED COMPONENTS ==========

const PageLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ControlsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${RM_COLORS.gray50};
  padding: 16px;
  border-radius: 8px;
  border: 1px solid ${RM_COLORS.gray200};
`;

const FormRow = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FormLabel = styled.span`
  font-size: ${p => p.theme.fontSizes[1]}px;
  font-weight: 400;
  color: ${p => p.theme.getColor("gray", 700)};
`;

const SelectInput = styled.select`
  padding: 0 12px;
  height: 36px;
  border-radius: 4px;
  border: 1px solid ${RM_COLORS.gray300};
  background: white;
  min-width: 150px;
  font-size: ${p => p.theme.fontSizes[1]}px;
  color: ${p => p.theme.getColor("gray", 700)};
`;

const PillsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const PillButton = styled.button<{ active: boolean; color: string }>`
  padding: 0 16px;
  height: 36px;
  border-radius: 20px;
  border: 2px solid ${p => (p.active ? p.color : "transparent")};
  background: ${p => (p.active ? p.color + "20" : RM_COLORS.gray100)};
  color: ${p => (p.active ? p.color : p.theme.getColor("gray", 700))};
  font-size: ${p => p.theme.fontSizes[1]}px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  &:before {
    content: "";
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${p => p.color};
  }
`;

const AddPillWrapper = styled.div`
  position: relative;
`;

const AddPillButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px dashed ${RM_COLORS.gray400};
  background: white;
  color: ${RM_COLORS.gray500};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  &:hover {
    border-color: ${RM_COLORS.blue};
    color: ${RM_COLORS.blue};
  }
`;

const BrokerDropdown = styled.div`
  position: absolute;
  top: 44px;
  left: 0;
  background: white;
  border-radius: 8px;
  box-shadow:
    0 4px 24px rgba(0, 0, 0, 0.12),
    0 0 0 1px rgba(63, 63, 68, 0.1);
  z-index: 100;
  min-width: 220px;
  overflow: hidden;
`;

const DropdownItem = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: none;
  cursor: ${p => (p.disabled ? "default" : "pointer")};
  font-size: 1.2rem;
  color: ${p => (p.disabled ? RM_COLORS.gray400 : RM_COLORS.gray700)};
  text-align: left;
  &:hover {
    background: ${p => (p.disabled ? "none" : RM_COLORS.gray100)};
  }
`;

const DropdownDot = styled.div<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${p => p.color};
  flex-shrink: 0;
`;

const RemovePillButton = styled.span`
  margin-left: 6px;
  font-size: 1.2rem;
  opacity: 0.5;
  cursor: pointer;
  &:hover {
    opacity: 1;
  }
`;

const CalendarWrapper = styled.div`
  display: flex;
  gap: 20px;
`;

const CalendarContainer = styled.div`
  flex: 1;
  /* Size to the viewport so the modal fits without its own scrollbar on normal
     screens; keep a usable floor so it scrolls inside the modal only on very
     small screens. ~280px accounts for the modal header, toolbar and padding. */
  height: clamp(320px, calc(100vh - 280px), 600px);

  .rbc-calendar {
    font-size: 1.2rem;
    color: ${RM_COLORS.gray900};
  }

  .rbc-toolbar button {
    font-family: inherit;
    font-size: 1.2rem;
    color: ${RM_COLORS.gray700};
  }

  .rbc-toolbar-label {
    font-size: 1.4rem;
    font-weight: 600;
    color: ${RM_COLORS.gray900};
  }

  .rbc-event {
    border-radius: 4px;
    cursor: pointer;
  }

  .rbc-event.readonly {
    opacity: 0.35;
    cursor: default;
    background-image: repeating-linear-gradient(
      45deg,
      transparent,
      transparent 8px,
      rgba(255, 255, 255, 0.4) 8px,
      rgba(255, 255, 255, 0.4) 16px
    );
  }
`;

const Sidebar = styled.div`
  width: 280px;
  background: ${RM_COLORS.gray50};
  border: 1px solid ${RM_COLORS.gray200};
  border-radius: 8px;
  padding: 16px;
  /* Keep in sync with CalendarContainer so both columns stay the same height. */
  max-height: clamp(320px, calc(100vh - 280px), 600px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const SidebarSlotsList = styled.div`
  /* Scrolls on its own so the total stays pinned; grow:0 keeps the total snug
     under the list when there are few slots instead of pushing it to the bottom. */
  flex: 0 1 auto;
  min-height: 0;
  overflow-y: auto;
`;

const SidebarTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 1.4rem;
  font-weight: 600;
  color: ${RM_COLORS.gray900};
  flex-shrink: 0;
`;

const DayCard = styled.div`
  background: white;
  border: 1px solid ${RM_COLORS.gray200};
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
`;

const DayHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const DayLabel = styled.span`
  font-weight: 600;
  font-size: 1.2rem;
  color: ${RM_COLORS.green};
  text-transform: uppercase;
`;

const SlotItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  font-size: 1.2rem;
  color: ${RM_COLORS.gray700};
`;

const DeleteSlotBtn = styled.button`
  background: none;
  border: none;
  color: ${RM_COLORS.gray300};
  cursor: pointer;
  font-size: 1.2rem;
  padding: 0 2px;
  &:hover {
    color: ${RM_COLORS.red};
  }
`;

const TotalCard = styled.div`
  background: ${RM_COLORS.greenBg};
  border: 1px solid ${RM_COLORS.greenBorder};
  border-radius: 8px;
  padding: 12px;
  /* Pinned footer: stays visible while the slots list scrolls above it. The
     top spacing keeps scrolling day cards from bumping against it. */
  flex-shrink: 0;
  margin-top: 16px;
`;

const DurationToggleGroup = styled.div`
  display: flex;
  gap: 6px;
`;

const DurationToggleBtn = styled.button<{ active: boolean }>`
  padding: 0 14px;
  height: 36px;
  border-radius: 6px;
  border: 1.5px solid ${p => (p.active ? RM_COLORS.green : RM_COLORS.gray300)};
  background: ${p => (p.active ? RM_COLORS.greenBg : RM_COLORS.white)};
  color: ${p =>
    p.active ? RM_COLORS.greenDeep : p.theme.getColor("gray", 700)};
  font-size: ${p => p.theme.fontSizes[1]}px;
  font-weight: ${p => (p.active ? 600 : 400)};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover {
    border-color: ${RM_COLORS.green};
  }
`;

// ========== INTERFACES ==========

interface IBroker {
  id: string;
  name: string;
  color: string;
}

export interface ICalendarSlot {
  id: string;
  title: string;
  start: Date;
  end: Date;
  userId: string;
  color: string;
  isReadonly?: boolean;
  isRentioVisit?: boolean;
}

interface IPropertyNativeCalendarProps {
  propertyId: number;
  propertyAddress?: string;
  onUpdateSlots: (
    slots: {
      start: string;
      end: string;
      userId: string;
      slotDurationMinutes?: number;
      capacityPerSlot?: number;
    }[],
  ) => void;
  onDirty?: () => void;
  defaultBrokerUserId?: number;
  onRemainingCountChange?: (remaining: number) => void;
}

// Calendar events (external busy blocks, planned visits, availability slots)
// may carry extra descriptive fields from the connected calendar.
type ICalendarEventDisplay = ICalendarSlot & {
  summary?: string;
  location?: string;
};

/**
 * Custom react-big-calendar event renderer. Shows the event title in the block
 * and a hover tooltip revealing the full title plus, when available, the
 * address/location of the appointment — so a broker can see what an existing
 * (busy) appointment is without leaving the planner. Defined at module scope so
 * it is not recreated on every calendar render.
 */
// react-big-calendar types `components.event` as `EventProps<object>`, so accept
// the loose `object` shape it passes and narrow to our display type internally.
const CalendarEvent: React.FC<{ event: object }> = ({ event }) => {
  const { title, summary, location } = event as ICalendarEventDisplay;
  // The block keeps its short label (e.g. "Not available (Broker)"); on hover we
  // reveal the actual appointment content — the real summary and location when
  // the calendar provides them, falling back to the label otherwise.
  const tooltipTitle = summary || title;
  return (
    <Tooltip
      placement="top"
      tooltipContentStyle={{
        backgroundColor: RM_COLORS.gray900,
        color: RM_COLORS.white,
        padding: "6px 10px",
        borderRadius: "6px",
        fontSize: "12px",
        lineHeight: "1.4",
        maxWidth: "260px",
      }}
      tooltipContent={
        <div>
          <div style={{ fontWeight: 600 }}>{tooltipTitle}</div>
          {location && (
            <div style={{ opacity: 0.85, marginTop: "2px" }}>{location}</div>
          )}
        </div>
      }
    >
      <span
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {title}
      </span>
    </Tooltip>
  );
};

// ========== COMPONENT ==========

export const PropertyNativeCalendar: React.FC<IPropertyNativeCalendarProps> = ({
  propertyId,
  propertyAddress,
  onUpdateSlots,
  onDirty,
  defaultBrokerUserId,
  onRemainingCountChange,
}) => {
  // Ensure moment uses the correct user language for all native translations
  moment.locale(
    String(getLocale()) === "en_US"
      ? "en-gb"
      : String(getLocale()).replace("_", "-").toLowerCase(),
  );

  const { user: currentUser } = useSelf();

  const [events, setEvents] = useState<ICalendarSlot[]>([]);
  const [bookedVisits, setBookedVisits] = useState<any[]>([]);
  const [isLoadingBrokers, setIsLoadingBrokers] = useState(true);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);

  const [brokers, setBrokers] = useState<IBroker[]>([]);
  const [allConnections, setAllConnections] = useState<IBroker[]>([]);
  const [activeBrokerId, setActiveBrokerId] = useState<string>("");
  const [showBrokerDropdown, setShowBrokerDropdown] = useState(false);
  const [pendingRemoveBrokerId, setPendingRemoveBrokerId] = useState<
    string | null
  >(null);
  const [duration, setDuration] = useState<number>(15);
  const [capacity, setCapacity] = useState<number>(1);

  const activeBroker = useMemo(
    () => brokers.find(b => b.id === activeBrokerId),
    [brokers, activeBrokerId],
  );

  // Editable (non-readonly) slots for the sidebar
  const editableSlots = useMemo(
    () => events.filter(e => !e.isReadonly),
    [events],
  );
  const busyEvents = useMemo(() => events.filter(e => e.isReadonly), [events]);

  // Count available and booked slots in a block, excluding time that overlaps with busy events
  const countRemainingSlots = useCallback(
    (slot: ICalendarSlot) => {
      let theoretical = 0;
      let booked = 0;
      const now = moment();
      const slotStart = moment(slot.start);
      const slotEnd = moment(slot.end);
      const cursor = slotStart.clone();

      while (cursor.clone().add(duration, "minutes").isSameOrBefore(slotEnd)) {
        const windowStart = cursor.clone();
        const windowEnd = cursor.clone().add(duration, "minutes");

        // Expired windows (already in the past) are no longer available
        // capacity — do not count them as bookable slots.
        if (windowEnd.isSameOrBefore(now)) {
          cursor.add(duration, "minutes");
          continue;
        }

        // Check overlap with busy events from the same broker only.
        // Other brokers' busy times should not affect this slot's availability.
        const overlapsBusy = busyEvents.some(busy => {
          if (busy.isRentioVisit) return false; // Native viewings do NOT block the capacity tracker
          if (busy.userId !== slot.userId) return false;
          const busyStart = moment(busy.start);
          const busyEnd = moment(busy.end);
          return windowStart.isBefore(busyEnd) && windowEnd.isAfter(busyStart);
        });

        if (!overlapsBusy) {
          theoretical += capacity;
          const bookedInWindow = bookedVisits.filter(v => {
            const vStart = moment(v.start);
            const vEnd = moment(v.end);
            return (
              vStart.isSameOrAfter(windowStart) &&
              vEnd.isSameOrBefore(windowEnd)
            );
          }).length;
          booked += bookedInWindow;
        }
        cursor.add(duration, "minutes");
      }
      return { theoretical, booked };
    },
    [busyEvents, duration, capacity, bookedVisits],
  );

  // Group editable slots by day for the sidebar
  const slotsByDay = useMemo(() => {
    const grouped: Record<string, ICalendarSlot[]> = {};
    editableSlots.forEach(slot => {
      const dayKey = moment(slot.start).format("YYYY-MM-DD");
      if (!grouped[dayKey]) grouped[dayKey] = [];
      grouped[dayKey].push(slot);
    });
    const sortedKeys = Object.keys(grouped).sort();
    return sortedKeys.map(key => ({
      dayKey: key,
      label: moment(key).format("dddd D MMMM"),
      slots: grouped[key].sort((a, b) => a.start.getTime() - b.start.getTime()),
    }));
  }, [editableSlots]);

  // Count total available and booked slots across all editable blocks
  const totalCounts = useMemo(() => {
    let t = 0;
    let b = 0;
    editableSlots.forEach(slot => {
      const counts = countRemainingSlots(slot);
      t += counts.theoretical;
      b += counts.booked;
    });
    return { theoretical: t, booked: b };
  }, [editableSlots, countRemainingSlots]);

  useEffect(() => {
    if (onRemainingCountChange) {
      onRemainingCountChange(totalCounts.theoretical - totalCounts.booked);
    }
  }, [totalCounts, onRemainingCountChange]);

  // Load broker connections
  const BROKER_COLORS = [
    RM_COLORS.green, // emerald
    RM_COLORS.blue, // blue
    RM_COLORS.amber, // amber
    RM_COLORS.purple, // violet
    RM_COLORS.red, // red
    RM_COLORS.brandAccent, // pink
    RM_COLORS.teal, // teal
    RM_COLORS.orange, // orange
  ];

  useEffect(() => {
    const loadBrokers = async () => {
      try {
        setIsLoadingBrokers(true);
        const [connRes, savedRes] = await Promise.all([
          api.get<any>("/cronofy/connections", { isV2: true }),
          api.get<any>(`/cronofy/properties/${propertyId}/availability`, {
            isV2: true,
          }),
        ]);
        const connections = connRes.data?.data || connRes.data || [];
        const connectedBrokers: IBroker[] = [];
        const seenIds = new Set<string>();

        connections
          .filter((c: any) => c.status === "CONNECTED")
          .forEach((c: any, idx: number) => {
            const id = String(c.userId || idx);
            if (!seenIds.has(id)) {
              seenIds.add(id);
              connectedBrokers.push({
                id,
                name: c.name || c.email || `Agenda ${idx + 1}`,
                color:
                  BROKER_COLORS[connectedBrokers.length % BROKER_COLORS.length],
              });
            }
          });
        setAllConnections(connectedBrokers);

        // Check which users already have saved blocks for this property
        const savedBlocksList =
          savedRes.data?.data?.blocks ||
          savedRes.data?.data ||
          savedRes.data ||
          [];
        const savedUserIds: string[] = [
          ...new Set<string>(savedBlocksList.map((b: any) => String(b.userId))),
        ];

        // Default brokers: all users who have saved blocks, + the logged-in user, + the administrative manager
        const currentUserId = String(currentUser?.id);
        const defaultBrokerIds = new Set([
          ...savedUserIds,
          currentUserId,
          ...(defaultBrokerUserId ? [String(defaultBrokerUserId)] : []),
        ]);
        let defaultBrokers = connectedBrokers.filter(b =>
          defaultBrokerIds.has(b.id),
        );

        // Create fallback broker entries for saved block users who are not in connections
        // (e.g. another agency member whose connection isn't visible to the current user)
        const matchedIds = new Set(defaultBrokers.map(b => b.id));
        savedUserIds.forEach((uid, idx) => {
          if (!matchedIds.has(uid)) {
            const block = savedBlocksList.find(
              (b: any) => String(b.userId) === uid,
            );
            defaultBrokers.push({
              id: uid,
              name: block?.brokerName || `Agenda ${uid}`,
              color:
                BROKER_COLORS[
                  (defaultBrokers.length + idx) % BROKER_COLORS.length
                ],
            });
          }
        });

        // Also ensure the current user is included if they have a connection
        if (!matchedIds.has(currentUserId)) {
          const currentConn = connectedBrokers.find(
            b => b.id === currentUserId,
          );
          if (
            currentConn &&
            !defaultBrokers.find(b => b.id === currentUserId)
          ) {
            defaultBrokers.push(currentConn);
          }
        }

        // If nobody matched (e.g. no saved blocks yet), just show the logged-in user
        if (defaultBrokers.length > 0) {
          setBrokers(defaultBrokers);
          // Set active to the property's administrative manager if provided, else logged-in user, else first
          const preferredUserId = defaultBrokerUserId
            ? String(defaultBrokerUserId)
            : currentUserId;
          const preferredBroker = defaultBrokers.find(
            b => b.id === preferredUserId,
          );
          const currentUserBroker = defaultBrokers.find(
            b => b.id === currentUserId,
          );
          setActiveBrokerId(
            (preferredBroker || currentUserBroker || defaultBrokers[0]).id,
          );
        } else if (connectedBrokers.length > 0) {
          setBrokers([connectedBrokers[0]]);
          setActiveBrokerId(connectedBrokers[0].id);
        }
      } catch (e) {
        console.error("Failed to load broker connections", e); // eslint-disable-line no-console
        setBrokers([]);
      } finally {
        setIsLoadingBrokers(false);
      }
    };
    loadBrokers();
  }, [propertyId]);

  const availableBrokersToAdd = useMemo(
    () => allConnections.filter(c => !brokers.some(b => b.id === c.id)),
    [allConnections, brokers],
  );

  const addBrokerToCalendar = (broker: IBroker) => {
    setBrokers(prev => [...prev, broker]);
    setActiveBrokerId(broker.id);
    setShowBrokerDropdown(false);
  };

  const removeBrokerFromCalendar = (brokerId: string) => {
    setPendingRemoveBrokerId(brokerId);
  };

  const confirmRemoveBroker = () => {
    if (!pendingRemoveBrokerId) return;
    const brokerId = pendingRemoveBrokerId;
    setPendingRemoveBrokerId(null);

    const newEvents = events.filter(e => e.userId !== brokerId);
    setEvents(newEvents);
    syncToParent(newEvents);
    if (onDirty) onDirty();

    setBrokers(prev => {
      const updated = prev.filter(b => b.id !== brokerId);
      if (activeBrokerId === brokerId && updated.length > 0) {
        setActiveBrokerId(updated[0].id);
      }
      return updated;
    });
  };

  // Close dropdown on click outside
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showBrokerDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setShowBrokerDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showBrokerDropdown]);

  // Load busy events + saved availability blocks
  useEffect(() => {
    if (brokers.length === 0) return;

    const loadBackgroundData = async () => {
      try {
        setIsLoadingEvents(true);
        const brokerIds = brokers.map(b => b.id).join(",");
        const from = moment().startOf("week").toISOString();
        const to = moment().add(1, "month").endOf("week").toISOString();

        // cspell:ignore freebusy
        // freebusy is non-fatal: a 401 JWT refresh or Cronofy auth failure
        // should not wipe the availability slots from the sidebar.
        const [busyRes, savedRes] = await Promise.all([
          api
            .get<any>(
              `/cronofy/properties/${propertyId}/freebusy?brokerIds=${brokerIds}&from=${from}&to=${to}`,
              { isV2: true },
            )
            .catch(() => ({ data: [] })),
          api.get<any>(`/cronofy/properties/${propertyId}/availability`, {
            isV2: true,
          }),
        ]);

        const busyBlocks = (busyRes.data?.data || busyRes.data || []).map(
          (b: any) => ({
            ...b,
            start: new Date(b.start),
            end: new Date(b.end),
          }),
        );

        const savedBlocksList =
          savedRes.data?.data?.blocks ||
          savedRes.data?.data ||
          savedRes.data ||
          [];
        const visitsList = savedRes.data?.data?.visits || [];
        setBookedVisits(visitsList);

        const mappedVisits = visitsList.map((v: any) => {
          const visitBroker = brokers.find(br => br.id === String(v.userId));
          return {
            id: `visit-${v.id}`,
            title: getLocalizedText("rentmatch.calendar_planned_visit"),
            start: new Date(v.start),
            end: new Date(v.end),
            userId: String(v.userId),
            color: visitBroker?.color || RM_COLORS.blue,
            status: "busy",
            isReadonly: true, // Bookings cannot be manually dragged
            isRentioVisit: true,
          };
        });

        // Google Free/Busy will return 'Busy' slots for Rentio visit events
        // (already booked viewings we track ourselves). We exclude those to
        // avoid double-counting them as external conflicts.
        // Note: we do NOT filter shadow availability blocks here — doing so
        // would incorrectly hide real conflicts from other properties whose
        // shadow events happen to overlap a newly saved block for this property.
        const filteredBusyBlocks = busyBlocks.filter((b: any) => {
          // Skip events that mirror a Rentio-tracked visit
          if (
            mappedVisits.some(
              (v: any) =>
                v.userId === String(b.brokerId) &&
                Math.abs(v.start.getTime() - new Date(b.start).getTime()) <
                  60000 &&
                Math.abs(v.end.getTime() - new Date(b.end).getTime()) < 60000,
            )
          )
            return false;

          return true;
        });

        // Assign each busy block the color of the broker it belongs to
        const coloredBusyBlocks = filteredBusyBlocks.map((b: any) => {
          const busyBroker = brokers.find(br => br.id === String(b.brokerId));
          const unavailableLabel = getLocalizedText(
            "rentmatch.calendar_not_available",
          );
          return {
            ...b,
            title: busyBroker
              ? `${unavailableLabel} (${busyBroker.name})`
              : unavailableLabel,
            userId: String(b.brokerId),
            color: busyBroker?.color || RM_COLORS.gray400,
            isReadonly: true,
          };
        });

        const savedBlocks = savedBlocksList.map((b: any) => {
          const broker = brokers.find(br => br.id === String(b.userId));
          const titlePrefix = propertyAddress
            ? `${getLocalizedText(
                "rentmatch.calendar_placeholder_visit",
              )} ${propertyAddress}`
            : getLocalizedText("rentmatch.calendar_placeholder_visit");
          return {
            id: b.id,
            title: `${titlePrefix} (${
              broker?.name || b.brokerName || "Broker"
            })`,
            start: new Date(b.start),
            end: new Date(b.end),
            userId: String(b.userId),
            color: broker?.color || RM_COLORS.gray400,
            isReadonly: false,
          };
        });

        if (savedBlocksList.length > 0) {
          const firstBlock = savedBlocksList[0];
          if (firstBlock.slotDurationMinutes)
            setDuration(Number(firstBlock.slotDurationMinutes));
          if (firstBlock.capacityPerSlot)
            setCapacity(Number(firstBlock.capacityPerSlot));
        }

        const activeBrokerIds = new Set(brokers.map(b => b.id));
        setEvents([
          ...coloredBusyBlocks.filter((b: ICalendarSlot) =>
            activeBrokerIds.has(b.userId),
          ),
          ...savedBlocks.filter((b: ICalendarSlot) =>
            activeBrokerIds.has(b.userId),
          ),
          ...mappedVisits,
        ]);
      } catch (e) {
        console.error("Failed to load calendar data", e); // eslint-disable-line no-console
        setEvents([]);
      } finally {
        setIsLoadingEvents(false);
      }
    };

    loadBackgroundData();
  }, [propertyId, brokers]);

  // Notify parent whenever editable slots change
  const syncToParent = useCallback(
    (updatedEvents: ICalendarSlot[]) => {
      const savableSlots = updatedEvents
        .filter(e => !e.isReadonly)
        .map(e => ({
          start: e.start.toISOString(),
          end: e.end.toISOString(),
          userId: e.userId,
          slotDurationMinutes: duration,
          capacityPerSlot: capacity,
        }));
      onUpdateSlots(savableSlots);
    },
    [duration, capacity, onUpdateSlots],
  );

  // Sync to parent immediately when duration or capacity settings change
  useEffect(() => {
    syncToParent(events);
  }, [duration, capacity, syncToParent]);

  const snapEndToDuration = useCallback(
    (start: Date, end: Date): Date => {
      const diffMins = moment(end).diff(moment(start), "minutes");
      let multiples = Math.round(diffMins / duration);
      if (multiples < 1) multiples = 1; // minimum 1 block length
      return moment(start)
        .add(multiples * duration, "minutes")
        .toDate();
    },
    [duration],
  );

  const handleSelectSlot = useCallback(
    ({ start, end, action }: { start: Date; end: Date; action: string }) => {
      if (!activeBroker) return;
      if (action === "click") return; // Single-click ignores. Drag and double-click are allowed.

      // Auto-snap the end to a multiple of the configured duration.
      const snappedEnd = snapEndToDuration(start, end);

      // Disallow creating slots in the past — a viewing can't be scheduled for
      // a moment that has already passed.
      if (moment(start).isBefore(moment())) {
        toast({
          heading: getLocalizedText("rentmatch.calendar_past_slot_error"),
          variation: "error",
        });
        return;
      }

      // Disallow overlap with existing blocks of any broker
      const overlap = editableSlots.some(
        s =>
          moment(start).isBefore(s.end) && moment(snappedEnd).isAfter(s.start),
      );
      if (overlap) {
        toast({
          heading: getLocalizedText("rentmatch.calendar_overlap_error_same"),
          variation: "error",
        });
        return;
      }

      const titlePrefix = propertyAddress
        ? `${getLocalizedText(
            "rentmatch.calendar_placeholder_visit",
          )} ${propertyAddress}`
        : getLocalizedText("rentmatch.calendar_placeholder_visit");
      const newSlot: ICalendarSlot = {
        id: `slot-${Date.now()}`,
        title: `${titlePrefix} (${activeBroker.name})`,
        start,
        end: snappedEnd,
        userId: activeBroker.id,
        color: activeBroker.color,
        isReadonly: false,
      };

      const newEvents = [...events, newSlot];
      setEvents(newEvents);
      syncToParent(newEvents);
      if (onDirty) onDirty();
    },
    [
      activeBroker,
      events,
      editableSlots,
      syncToParent,
      snapEndToDuration,
      onDirty,
    ],
  );

  // Delete a single slot by id
  const deleteSlot = useCallback(
    (slotId: string) => {
      const newEvents = events.filter(e => e.id !== slotId);
      setEvents(newEvents);
      syncToParent(newEvents);
      if (onDirty) onDirty();
    },
    [events, syncToParent, onDirty],
  );

  // Handle resizing of an existing slot
  const handleEventResize = useCallback(
    ({ event, start, end }: any) => {
      if (event.isReadonly) return;

      // Force the new size to snap to a multiple of configured duration
      const snappedEnd = snapEndToDuration(start, end);

      // Disallow overlap with existing blocks of any broker
      const overlap = editableSlots.some(
        s =>
          s.id !== event.id &&
          moment(start).isBefore(s.end) &&
          moment(snappedEnd).isAfter(s.start),
      );
      if (overlap) {
        toast({
          heading: getLocalizedText("rentmatch.calendar_overlap_error_other"),
          variation: "error",
        });
        return;
      }

      const newEvents = events.map(e =>
        e.id === event.id ? { ...e, start, end: snappedEnd } : e,
      );
      setEvents(newEvents);
      syncToParent(newEvents);
      if (onDirty) onDirty();
    },
    [events, editableSlots, syncToParent, snapEndToDuration, onDirty],
  );

  // Handle moving of an existing slot
  const handleEventDrop = useCallback(
    ({ event, start, end }: any) => {
      if (event.isReadonly) return;
      const snappedEnd = snapEndToDuration(start, end);

      const overlap = editableSlots.some(
        s =>
          s.id !== event.id &&
          moment(start).isBefore(s.end) &&
          moment(snappedEnd).isAfter(s.start),
      );
      if (overlap) {
        toast({
          heading: getLocalizedText("rentmatch.calendar_overlap_error_other"),
          variation: "error",
        });
        return;
      }

      const newEvents = events.map(e =>
        e.id === event.id ? { ...e, start, end: snappedEnd } : e,
      );
      setEvents(newEvents);
      syncToParent(newEvents);
      if (onDirty) onDirty();
    },
    [events, editableSlots, syncToParent, snapEndToDuration, onDirty],
  );

  const eventStyleGetter = (event: any) => {
    const style: React.CSSProperties = {
      backgroundColor: event.color,
      borderColor: event.color,
    };
    return { style, className: event.isReadonly ? "readonly" : "" };
  };

  if (isLoadingBrokers) {
    return (
      <PageLayout>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <TextStyle variation="subdued">
            {getLocalizedText("rentmatch.calendar_loading_agendas")}
          </TextStyle>
        </div>
      </PageLayout>
    );
  }

  if (brokers.length === 0) {
    return (
      <PageLayout>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <TextStyle variation="strong">
            {getLocalizedText("rentmatch.calendar_no_agendas")}
          </TextStyle>
          <br />
          <TextStyle variation="subdued" size="small">
            {getLocalizedText("rentmatch.calendar_connect_hint")}
          </TextStyle>
        </div>
      </PageLayout>
    );
  }

  const pendingBroker = pendingRemoveBrokerId
    ? brokers.find(b => b.id === pendingRemoveBrokerId)
    : null;
  const pendingBrokerHasSlots = pendingRemoveBrokerId
    ? editableSlots.some(s => s.userId === pendingRemoveBrokerId)
    : false;

  // Total-card label: booked count for unlimited capacity, a "fully booked"
  // message when every bookable spot is taken, else the remaining capacity
  // with correct singular/plural.
  let totalCardLabel: string;
  if (capacity <= 0) {
    totalCardLabel = `${getLocalizedText("rentmatch.calendar_total")}: ${
      totalCounts.booked
    } ${getLocalizedText("rentmatch.calendar_visits_booked_unlimited_short")}`;
  } else if (
    totalCounts.theoretical > 0 &&
    totalCounts.booked >= totalCounts.theoretical
  ) {
    totalCardLabel = getLocalizedText("rentmatch.calendar_slots_full");
  } else {
    totalCardLabel = `${getLocalizedText(
      "rentmatch.calendar_total",
    )}: ${getLocalizedText("rentmatch.calendar_visitors_possible_count", {
      count: String(totalCounts.theoretical),
      s: totalCounts.theoretical === 1 ? "" : "s",
    })}`;
  }

  return (
    <>
      {pendingRemoveBrokerId && (
        <Modal
          heading={getLocalizedText("rentmatch.calendar_remove_broker_heading")}
          onClose={() => setPendingRemoveBrokerId(null)}
          actions={[
            {
              content: getLocalizedText("system.cancel"),
              appearance: "outline",
              onClick: () => setPendingRemoveBrokerId(null),
            },
            {
              content: getLocalizedText(
                "rentmatch.calendar_remove_broker_confirm",
              ),
              appearance: "primary",
              onClick: confirmRemoveBroker,
            },
          ]}
        >
          <TextStyle>
            {pendingBrokerHasSlots
              ? getLocalizedText(
                  "rentmatch.calendar_remove_broker_body_with_slots",
                  { name: pendingBroker?.name ?? "" },
                )
              : getLocalizedText("rentmatch.calendar_remove_broker_body", {
                  name: pendingBroker?.name ?? "",
                })}
          </TextStyle>
        </Modal>
      )}
      <PageLayout>
        <ControlsHeader>
          <FormRow>
            <FormGroup>
              <FormLabel>
                {getLocalizedText("rentmatch.calendar_slot_duration")}
              </FormLabel>
              <DurationToggleGroup>
                {[15, 20, 30, 60].map(d => (
                  <DurationToggleBtn
                    key={d}
                    active={duration === d}
                    onClick={() => {
                      setDuration(d);
                      if (onDirty) onDirty();
                    }}
                  >
                    {d < 60 ? `${d} min` : `${d} min`}
                  </DurationToggleBtn>
                ))}
              </DurationToggleGroup>
            </FormGroup>
            <FormGroup>
              <FormLabel>
                {getLocalizedText("rentmatch.calendar_visitors_per_slot")}
              </FormLabel>
              <SelectInput
                value={capacity}
                onChange={e => {
                  setCapacity(Number(e.target.value));
                  if (onDirty) onDirty();
                }}
              >
                <option value={1}>
                  1 {getLocalizedText("rentmatch.calendar_visitor")}
                </option>
                <option value={2}>
                  2 {getLocalizedText("rentmatch.calendar_visitors")}
                </option>
                <option value={3}>
                  3 {getLocalizedText("rentmatch.calendar_visitors")}
                </option>
                <option value={5}>
                  5 {getLocalizedText("rentmatch.calendar_visitors")}
                </option>
                <option value={10}>
                  10 {getLocalizedText("rentmatch.calendar_visitors")}
                </option>
              </SelectInput>
            </FormGroup>
            <FormGroup>
              <FormLabel>
                {getLocalizedText("rentmatch.calendar_agendas_label")}
              </FormLabel>
              <PillsContainer>
                {brokers.map(b => (
                  <PillButton
                    key={b.id}
                    active={activeBrokerId === b.id}
                    color={b.color}
                    onClick={() => setActiveBrokerId(b.id)}
                  >
                    {b.name}
                    {brokers.length > 1 && (
                      <RemovePillButton
                        onClick={e => {
                          e.stopPropagation();
                          removeBrokerFromCalendar(b.id);
                        }}
                      >
                        ✕
                      </RemovePillButton>
                    )}
                  </PillButton>
                ))}
                {availableBrokersToAdd.length > 0 && (
                  <AddPillWrapper ref={dropdownRef}>
                    <AddPillButton
                      title={getLocalizedText("rentmatch.calendar_add_agenda")}
                      onClick={() => setShowBrokerDropdown(!showBrokerDropdown)}
                    >
                      +
                    </AddPillButton>
                    {showBrokerDropdown && (
                      <BrokerDropdown>
                        {availableBrokersToAdd.map(c => (
                          <DropdownItem
                            key={c.id}
                            onClick={() => addBrokerToCalendar(c)}
                          >
                            <DropdownDot color={c.color} />
                            {c.name}
                          </DropdownItem>
                        ))}
                      </BrokerDropdown>
                    )}
                  </AddPillWrapper>
                )}
              </PillsContainer>
            </FormGroup>
          </FormRow>
        </ControlsHeader>

        <CalendarWrapper>
          <CalendarContainer>
            <DnDCalendar
              localizer={localizer}
              culture={
                String(getLocale()) === "en_US"
                  ? "en-gb"
                  : String(getLocale()).replace("_", "-").toLowerCase()
              }
              formats={{
                dayFormat: "ddd D",
                dayRangeHeaderFormat: ({ start, end }: any) => {
                  if (moment(start).isSame(moment(end), "month")) {
                    return `${moment(start).format("D")} – ${moment(end).format(
                      "D MMMM",
                    )}`;
                  } else if (moment(start).isSame(moment(end), "year")) {
                    return `${moment(start).format("D MMMM")} – ${moment(
                      end,
                    ).format("D MMMM")}`;
                  } else {
                    return `${moment(start).format("D MMM YYYY")} – ${moment(
                      end,
                    ).format("D MMM YYYY")}`;
                  }
                },
              }}
              messages={{
                today: getLocalizedText("system.today"),
                previous: getLocalizedText("system.previous"),
                next: getLocalizedText("system.next"),
                month: getLocalizedText("system.month"),
                week: getLocalizedText("system.week"),
                day: getLocalizedText("system.day"),
                agenda: getLocalizedText("system.agenda"),
                date: getLocalizedText("system.date"),
                time: getLocalizedText("system.time"),
                event: getLocalizedText("system.event"),
              }}
              events={events}
              draggableAccessor={(event: any) => !event.isReadonly}
              resizableAccessor={(event: any) => !event.isReadonly}
              startAccessor={(event: any) => event.start}
              endAccessor={(event: any) => event.end}
              defaultView="week"
              views={["week", "day"]}
              selectable={true}
              resizable={true}
              onSelectSlot={handleSelectSlot}
              onEventResize={handleEventResize}
              onEventDrop={handleEventDrop}
              eventPropGetter={eventStyleGetter}
              components={{ event: CalendarEvent }}
              step={15}
              timeslots={4}
              min={new Date(0, 0, 0, 8, 0, 0)}
              max={new Date(0, 0, 0, 22, 0, 0)}
            />
          </CalendarContainer>

          <Sidebar>
            <SidebarTitle>
              {getLocalizedText("rentmatch.calendar_selected_slots")}
            </SidebarTitle>

            <SidebarSlotsList>
              {(() => {
                if (isLoadingEvents) {
                  return (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        padding: "30px 0",
                        gap: "12px",
                      }}
                    >
                      <Loading size={ELoadingSize.Small} />
                      <div
                        style={{ color: RM_COLORS.gray400, fontSize: "13px" }}
                      >
                        {getLocalizedText("rentmatch.calendar_loading_slots")}
                      </div>
                    </div>
                  );
                }
                if (slotsByDay.length === 0) {
                  return (
                    <div
                      style={{
                        color: RM_COLORS.gray400,
                        fontSize: "13px",
                        textAlign: "center",
                        padding: "20px 0",
                      }}
                    >
                      {getLocalizedText("rentmatch.calendar_select_slots_hint")}
                    </div>
                  );
                }
                return null;
              })()}

              {slotsByDay.map(day => {
                let dayTheoretical = 0;
                let dayBooked = 0;
                day.slots.forEach(s => {
                  const counts = countRemainingSlots(s);
                  dayTheoretical += counts.theoretical;
                  dayBooked += counts.booked;
                });

                return (
                  <DayCard key={day.dayKey}>
                    <DayHeader>
                      <div>
                        <DayLabel>{day.label}</DayLabel>
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: RM_COLORS.gray900,
                          }}
                        >
                          {capacity > 0
                            ? getLocalizedText(
                                "rentmatch.calendar_visits_booked_of",
                                {
                                  booked: String(dayBooked),
                                  total: String(dayTheoretical),
                                },
                              ) ||
                              `${dayBooked} of the ${dayTheoretical} visits booked`
                            : getLocalizedText(
                                "rentmatch.calendar_visits_booked_unlimited",
                                { booked: String(dayBooked) },
                              ) || `${dayBooked} visits booked (unlimited)`}
                        </div>
                      </div>
                    </DayHeader>
                    {day.slots.map(slot => {
                      const slotCounts = countRemainingSlots(slot);
                      const isExpired = moment(slot.end).isSameOrBefore(
                        moment(),
                      );
                      // theoretical === 0 on a non-expired slot means every
                      // window overlaps a busy calendar event; on an expired
                      // slot it just means the slot lies in the past — those
                      // need different messaging.
                      const isFullyBlocked =
                        !isExpired && slotCounts.theoretical === 0;
                      let slotWarning: string | null = null;
                      if (isExpired) {
                        slotWarning = getLocalizedText(
                          "rentmatch.calendar_past_slot_tooltip",
                        );
                      } else if (isFullyBlocked) {
                        slotWarning = getLocalizedText(
                          "rentmatch.calendar_busy_conflict_tooltip",
                        );
                      }
                      return (
                        <SlotItem key={slot.id}>
                          <span>
                            {moment(slot.start).format("HH:mm")} –{" "}
                            {moment(slot.end).format("HH:mm")}
                            {slotWarning && (
                              <WarningTooltip tooltipContent={slotWarning} />
                            )}
                            <span
                              style={{
                                marginLeft: "8px",
                                fontSize: "12px",
                                color: RM_COLORS.gray500,
                                fontWeight: 400,
                              }}
                            >
                              {brokers.find(b => b.id === slot.userId)?.name ||
                                "Broker"}
                            </span>
                          </span>
                          <DeleteSlotBtn
                            onClick={() => deleteSlot(slot.id)}
                            title={getLocalizedText(
                              "rentmatch.calendar_delete_slot",
                            )}
                          >
                            ✕
                          </DeleteSlotBtn>
                        </SlotItem>
                      );
                    })}
                  </DayCard>
                );
              })}
            </SidebarSlotsList>

            {editableSlots.length > 0 && (
              <TotalCard>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: "15px",
                    color: RM_COLORS.gray900,
                  }}
                >
                  {totalCardLabel}
                </div>
                {capacity > 0 && totalCounts.theoretical > 0 && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: RM_COLORS.gray500,
                      marginTop: "2px",
                    }}
                  >
                    {`(${totalCounts.theoretical / capacity} ${getLocalizedText(
                      "rentmatch.calendar_slots_label",
                    )} × ${capacity} ${getLocalizedText(
                      "rentmatch.calendar_visitors_per_slot_short",
                    )})`}
                  </div>
                )}
              </TotalCard>
            )}
          </Sidebar>
        </CalendarWrapper>
      </PageLayout>
    </>
  );
};
